import apiClient from './apiClient';
import { clearToken } from './tokenStorage';
import type { AuthUser } from './types';

// Re-Exports nur fuer das, was Konsumenten tatsaechlich ueber den
// authService beziehen (AuthContext: getToken, AuthCallbackPage: setToken).
export { getToken, setToken } from './tokenStorage';

/** Backend-Origin im Development (CRA-Dev-Server ohne Runtime-Config). */
const DEV_BACKEND_ORIGIN = 'http://localhost:8044';

/**
 * Spring-Security-Pfad, der den Google-OAuth2-Flow startet.
 * Relativ (ohne fuehrenden '/'), damit er per new URL() an einen
 * konfigurierten URL-Praefix inkl. Pfad-Anteil angehaengt werden kann.
 */
const GOOGLE_OAUTH_PATH = 'oauth2/authorization/google';

/**
 * sessionStorage-Schluessel fuer den Deep-Link (Pfad + Query), der nach
 * erfolgreichem Login angesteuert werden soll.
 */
export const POST_LOGIN_REDIRECT_STORAGE_KEY = 'mvgenMgmnt.postLoginRedirect';

/**
 * Laedt den aktuell angemeldeten Benutzer vom Backend.
 * Wirft bei fehlendem/ungueltigem Token (401) einen Axios-Fehler.
 */
export async function fetchMe(): Promise<AuthUser> {
  const response = await apiClient.get<AuthUser>('/api/auth/me');
  return response.data;
}

/** Haengt einen '/' an, falls die URL nicht bereits damit endet. */
function ensureTrailingSlash(url: string): string {
  return url.endsWith('/') ? url : `${url}/`;
}

/**
 * URL, die den Google-OAuth2-Login startet.
 *
 * Ermittlung:
 * 1. Runtime-Config (window.__RUNTIME_CONFIG__.REACT_APP_API_URL), falls
 *    gesetzt: der vollstaendige konfigurierte URL-Praefix wird genutzt
 *    (inkl. eines evtl. vorhandenen Pfad-Anteils, z. B. hinter einem
 *    Reverse-Proxy), nicht nur der Origin
 * 2. Development (CRA): fester Backend-Origin http://localhost:8044
 * 3. Produktion ohne Runtime-Config: relativer Pfad, nginx proxied /oauth2/
 */
export function loginUrl(): string {
  const configuredApiUrl = window.__RUNTIME_CONFIG__?.REACT_APP_API_URL;
  if (configuredApiUrl !== undefined && configuredApiUrl !== '') {
    try {
      return new URL(GOOGLE_OAUTH_PATH, ensureTrailingSlash(configuredApiUrl)).toString();
    } catch {
      // Ungueltige Runtime-Config -> auf die Fallbacks unten zurueckfallen
    }
  }
  if (process.env.NODE_ENV === 'development') {
    return `${DEV_BACKEND_ORIGIN}/${GOOGLE_OAUTH_PATH}`;
  }
  return `/${GOOGLE_OAUTH_PATH}`;
}

/** Meldet den Benutzer lokal ab, indem das JWT verworfen wird. */
export function logout(): void {
  clearToken();
}

/**
 * Merkt sich den angeforderten internen Pfad (Pfad + Query), zu dem nach
 * erfolgreichem Login navigiert werden soll (Deep-Link-Erhalt).
 */
export function savePostLoginRedirect(path: string): void {
  window.sessionStorage.setItem(POST_LOGIN_REDIRECT_STORAGE_KEY, path);
}

/**
 * Liest den gemerkten Deep-Link und entfernt den Eintrag aus der
 * sessionStorage. Liefert nur interne Pfade (muss mit '/' beginnen,
 * darf nicht mit '//' beginnen); sonst bzw. ohne Eintrag null.
 */
export function consumePostLoginRedirect(): string | null {
  const path = window.sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  window.sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
  if (path !== null && path.startsWith('/') && !path.startsWith('//')) {
    return path;
  }
  return null;
}
